import {Document, Font, Image, Page, Text, View} from '@react-pdf/renderer'
import {ChatExportStyles as styles} from './ChatExportStyles'
import PDFLogo from '../../PDFElements/PDFLogo'
import Footer from '../../PDFElements/Footer'
import PropTypes from 'prop-types'
import {isColumnNumeric} from '../../../shared/helpers/tableUtils'
import OpenSansRegular from '../../../fonts/open-sans/OpenSans-Regular.ttf'
import OpenSansItalic from '../../../fonts/open-sans/OpenSans-Italic.ttf'
import OpenSansSemiBold from '../../../fonts/open-sans/OpenSans-SemiBold.ttf'
import OpenSansBold from '../../../fonts/open-sans/OpenSans-Bold.ttf'

Font.register({
  family: 'Open Sans',
  fonts: [
    {src: OpenSansRegular},
    {src: OpenSansItalic, fontStyle: 'italic'},
    {src: OpenSansSemiBold, fontWeight: 600},
    {src: OpenSansBold, fontWeight: 700}
  ]
})

// Register bold font as separate family
Font.register({
  family: 'OpenSansBold',
  src: OpenSansBold
})

const ChatExport = ({exportTimeStamp, messages, prismProgram = 'HealthPrism'}) => {
  const shouldBreak = (value) => {
    if (!value) return false
    const charLimit = 2000
    const lineLimit = 25
    const actualLines = value.split(/\r?\n/).length
    return value.length > charLimit || actualLines > lineLimit
  }

  const handleBoldTags = (id, text) => {
    if (!text) {
      return text
    }
    let normalizedText = text.replaceAll('\r\n', '\n').replaceAll('\r', '\n')

    // Strip div tags but keep content
    normalizedText = normalizedText.replaceAll(/<\/?div[^>]*>/gi, '')

    // Convert HTML span tags with font-weight to markdown bold
    normalizedText = normalizedText.replaceAll(
      /<span[^>]*style="[^"]*font-weight:\s*(?:600|700|bold)[^"]*"[^>]*>(.*?)<\/span>/gi,
      '**$1**'
    )

    // Handle other HTML bold tags
    normalizedText = normalizedText.replaceAll(/<(b|strong)>(.*?)<\/\1>/gi, '**$2**')

    const parts = normalizedText.split(/(\*\*.*?\*\*)/g)

    // Use single Text component with nested Text for bold parts
    return (
      <Text key={id}>
        {parts.map((part, index) => {
          const match = part.match(/\*\*(.*?)\*\*/)
          if (match) {
            return (
              <Text
                style={{
                  fontFamily: 'OpenSansBold'
                }}
                key={`${id}-${index}`}
              >
                {match[1]}
              </Text>
            )
          }
          return part
        })}
      </Text>
    )
  }

  const parseMarkdownTable = (tableText) => {
    const normalizedText = tableText.replaceAll('\r\n', '\n').replaceAll('\r', '\n')
    const lines = normalizedText
      .trim()
      .split('\n')
      .filter((line) => line.trim() !== '')

    const headerLine = lines[0]

    let separatorIndex = -1
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].includes('-') && lines[i].includes('|')) {
        separatorIndex = i
        break
      }
    }

    const dataStartIndex = separatorIndex === -1 ? 1 : separatorIndex + 1
    const dataLines = lines.slice(dataStartIndex)

    const headers = headerLine
      .split('|')
      .map((cell) => cell.trim().replaceAll(String.raw`\_`, '_'))
      .filter((cell) => cell !== '')

    const rows = dataLines
      .filter((line) => line.includes('|'))
      .map((line) =>
        line
          .split('|')
          .map((cell) => cell.trim().replaceAll(String.raw`\_`, '_'))
          .filter((cell) => cell !== '')
      )
      .filter((row) => row.length > 0)

    return {headers, rows}
  }

  const renderTable = (tableData, messageId) => {
    const {headers, rows} = tableData
    const colWidth = 100 / headers.length

    return (
      <View style={styles.table} key={`table-${messageId}`}>
        <View style={styles.tableRow}>
          {headers.map((header, index) => {
            const isLastColumn = index === headers.length - 1
            const isNumericColumn = isColumnNumeric(rows, index)

            const headerStyle = {...styles.tableColHeader, width: `${colWidth}%`}

            if (isLastColumn) {
              headerStyle.borderRightWidth = 0
            }

            const textStyle = {
              ...styles.tableCellHeader,
              textAlign: isNumericColumn ? 'right' : 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }

            return (
              <View style={headerStyle} key={`header-${header}-${index}`}>
                <Text style={textStyle}>{header}</Text>
              </View>
            )
          })}
        </View>

        {rows.map((row, rowIndex) => (
          <View style={styles.tableRow} key={`row-${row.join('-')}-${rowIndex}`}>
            {row.map((cell, cellIndex) => {
              const isLastRow = rowIndex === rows.length - 1
              const isLastColumn = cellIndex === row.length - 1
              const isNumeric = /^[\d,.-]+$/.test(cell?.toString().trim())

              const cellStyle = {...styles.tableCol, width: `${colWidth}%`}

              if (isLastRow) {
                cellStyle.borderBottomWidth = 0
              }

              if (isLastColumn) {
                cellStyle.borderRightWidth = 0
              }

              const textStyle = {
                ...styles.tableCell,
                textAlign: isNumeric ? 'right' : 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }

              return (
                <View style={cellStyle} key={`cell-${cell}-${rowIndex}-${cellIndex}`}>
                  <Text style={textStyle}>{cell}</Text>
                </View>
              )
            })}
          </View>
        ))}
      </View>
    )
  }

  const handleTextWithTables = (id, text) => {
    if (!text) return text

    const normalizedText = text.replaceAll('\r\n', '\n').replaceAll('\r', '\n')
    const tableRegex = /^\|.*\|$\n^\|[-\s|:]+\|$\n(^\|.*\|$\n?)+/gm

    const result = []
    let lastIndex = 0
    let match
    let hasTable = false

    while ((match = tableRegex.exec(normalizedText)) !== null) {
      hasTable = true

      if (match.index > lastIndex) {
        const beforeTableText = normalizedText.substring(lastIndex, match.index).trim()
        if (beforeTableText) {
          result.push(<Text key={`text-before-${result.length}`}>{handleBoldTags(`${id}-text`, beforeTableText)}</Text>)
        }
      }

      const tableData = parseMarkdownTable(match[0])
      result.push(renderTable(tableData, `${id}-table-${result.length}`))

      lastIndex = match.index + match[0].length
    }

    if (lastIndex < normalizedText.length) {
      const afterTableText = normalizedText.substring(lastIndex).trim()
      if (afterTableText) {
        result.push(
          <Text key={`text-after-${result.length}`}>{handleBoldTags(`${id}-text-after`, afterTableText)}</Text>
        )
      }
    }

    if (!hasTable) {
      return handleBoldTags(id, text)
    }

    return result
  }

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        {PDFLogo}
        <View style={styles.timeStampContainer}>
          <Text style={styles.textRegular}>{exportTimeStamp}</Text>
        </View>
        <View style={styles.styledChatExportContainer}>
          <View style={styles.styledChatExportInsideContainer}>
            <Text style={styles.brandingtextHeading}>{prismProgram} Agent</Text>
            <View style={styles.betaBranding}>
              <Text>Beta</Text>
            </View>
          </View>
          <View>
            {messages
              ? messages.map((msgObj) => {
                  return (
                    <View key={msgObj.id}>
                      {msgObj.type === 'Bot' && msgObj.responseText && (
                        <View break={shouldBreak(msgObj.responseText) ? '' : 'avoid'}>
                          <Text style={{...styles.label, ...styles.botLabel}}>{msgObj.label}</Text>
                          <View style={styles.botMessage}>
                            {handleTextWithTables(msgObj.id, msgObj.responseText)}
                            {msgObj.aiInfoText && <Text style={styles.aiInfoText}>{msgObj.aiInfoText}</Text>}
                          </View>
                        </View>
                      )}
                      {msgObj.type === 'Bot' && msgObj.graphURL && (
                        <View break='avoid' style={styles.botMessage}>
                          <Image style={styles.chart} src={msgObj.graphURL} cache={false} />
                          {msgObj.aiInfoText && <Text style={styles.aiInfoText}>{msgObj.aiInfoText}</Text>}
                        </View>
                      )}
                      {msgObj.type === 'User' && (
                        <View break={shouldBreak(msgObj.message) ? '' : 'avoid'}>
                          <Text style={{...styles.label, ...styles.userLabel}}>{msgObj.label}</Text>
                          <Text style={styles.userMessage}>{msgObj.message}</Text>
                        </View>
                      )}
                    </View>
                  )
                })
              : null}
          </View>
        </View>
        {Footer}
      </Page>
    </Document>
  )
}

ChatExport.propTypes = {
  exportTimeStamp: PropTypes.string,
  messages: PropTypes.array,
  prismProgram: PropTypes.string
}
export default ChatExport

/* ChatExport Component Functions Documentation:

  FONT CONFIGURATION:
  - Font.register: Registers Open Sans font family with multiple weights and styles (Regular, Italic, SemiBold, Bold) for PDF rendering.

  PDF LAYOUT & PAGINATION:
  - shouldBreak: Determines page break logic based on content length (2500 chars) or line count (30 lines) to prevent content overflow.

  TEXT PROCESSING & FORMATTING:
  - handleBoldTags: Processes markdown bold syntax (**text**) and converts to styled PDF Text components with unique keys for React rendering.
  - handleTextWithTables: Master text processor that detects markdown tables and splits content into text/table sections for proper PDF layout.

  TABLE PROCESSING & RENDERING:
  - parseMarkdownTable: Parses markdown table syntax into structured data with headers and rows, handles escaped characters and malformed tables.
  - renderTable: Converts table data into PDF-compatible View/Text components with responsive column widths and proper styling.

  DOCUMENT STRUCTURE & LAYOUT:
  - Document/Page: Main PDF document container with A4 page size and standardized styling from ChatExportStyles.
  - Header section with program logo, timestamp, and branding elements including Beta designation.
  - Message iteration with conditional rendering based on message type (User/Bot) and content type (text/charts).

  MESSAGE TYPE HANDLING:
  - Bot Messages: Handles both text responses and chart images with AI disclaimer text integration.
  - User Messages: Renders user input with proper labeling and page break management.
  - Chart Integration: Embeds chart images with caching disabled for dynamic content and AI info text display.

  CONTENT FORMATTING FEATURES:
  - Bold text support through markdown syntax processing with proper PDF styling.
  - Table rendering with equal column distribution and responsive design for PDF format.
  - Page break optimization to prevent content splitting across pages inappropriately.
  - AI disclaimer integration for transparency in bot responses.

  STYLING & DESIGN:
  - Imports comprehensive styles from ChatExportStyles for consistent branding and typography.
  - Responsive table layout with calculated column widths based on header count.
  - Proper spacing and padding for readability in PDF format.
  - Beta branding integration with visual indicators.

  ERROR HANDLING & EDGE CASES:
  - Graceful handling of empty or null text content in all processing functions.
  - Fallback to original text formatting when no tables are detected.
  - Safe content parsing with regex validation for markdown table detection.
  - Unique key generation for React components to prevent rendering conflicts.

  INTEGRATION POINTS:
  - Uses PDFLogo and Footer components for consistent document structure.
  - Integrates with ChatExportStyles for centralized styling management.
  - Supports both HealthPrism and PeoplePrism branding through prismProgram prop.
  - Compatible with @react-pdf/renderer library for PDF generation.

  PERFORMANCE CONSIDERATIONS:
  - Content-based key generation for efficient React re-rendering.
  - Optimized table parsing with early returns for non-table content.
  - Efficient regex processing for markdown detection and parsing.
  - Lazy evaluation of page breaks based on actual content dimensions.
*/
